<script setup lang="ts">
/**
 * WhatsApp's "Dados da mensagem": the bubble on top, then when it was read, played (voice notes),
 * delivered and sent, from WhatsApp's own receipts, and what Meta charged for it. Failed sends
 * show why and Meta's code. An assistant's message also shows how it answered (decision 0035,
 * D13): the skills, knowledge and tools behind it and how much of the conversation it read.
 */
import { computed, ref, watch } from "vue";
import { RouterLink } from "vue-router";
import Modal from "./Modal.vue";
import Icon from "./Icon.vue";
import MessageBubble from "./MessageBubble.vue";
import MessageStatus from "./MessageStatus.vue";
import { messageDelivery } from "../composables/messageDelivery";
import { metaChargeText } from "../composables/metaPricing";
import { dateLabel, localDate } from "../composables/chatTimeline";
import type { Message } from "../data/demo";
import { skillsApi, type ReplyTrace } from "../api/skills";
import { can } from "../composables/permissions";
import { modelLabel, modelName } from "../composables/aiModels";
import { locale, t } from "../i18n";

const props = defineProps<{
  open: boolean;
  message: Message | null;
  /** The company and conversation, to ask an assistant's message how it answered. */
  slug?: string;
  conversationId?: string | null;
}>();
const trace = ref<ReplyTrace | null>(null);
watch(
  () => [props.open, props.message?.id] as const,
  async ([open]) => {
    trace.value = null;
    const m = props.message;
    if (!open || !m?.byAssistant || !props.slug || !props.conversationId)
      return;
    try {
      trace.value = (
        await skillsApi.trace(props.slug, props.conversationId, m.id)
      ).trace;
    } catch {
      /* older replies have none */
    }
  },
  { immediate: true },
);
/** "OpenAI · GPT-6 Astra · raciocínio baixo", and the model it answered for when one failed. */
const traceModel = computed(() => {
  const m = trace.value?.model;
  if (!m) return "";
  const effort = m.settings?.effort;
  return [
    modelLabel(m),
    effort
      ? t(`aimodels.level.${effort}` as never).toLocaleLowerCase(locale.value)
      : "",
  ]
    .filter(Boolean)
    .join(" · ");
});
const traceTime = computed(() =>
  trace.value?.ms === undefined
    ? ""
    : `${(trace.value.ms / 1000).toLocaleString(locale.value, { maximumFractionDigits: 1 })} s`,
);
const traceSkills = computed(() =>
  (trace.value?.skills ?? []).map((s) =>
    t(`message.trace.skill_${s.when}`, { name: s.name }),
  ),
);
const traceContext = computed(() => {
  const c = trace.value?.context;
  if (!c) return "";
  const n = (v: number) => new Intl.NumberFormat(locale.value).format(v);
  const parts = [t("message.trace.read", { n: n(c.messages) })];
  if (c.previous) parts.push(t("message.trace.previous", { n: n(c.previous) }));
  if (c.inputTokens && c.cachedTokens)
    parts.push(
      t("message.trace.cached", {
        percent: Math.round((c.cachedTokens / c.inputTokens) * 100),
      }),
    );
  return parts.join(" · ");
});
const emit = defineEmits<{ close: []; resend: [messageId: string] }>();

const delivery = computed(() =>
  props.message ? messageDelivery(props.message) : "pending",
);
const charge = computed(() =>
  props.message?.pricing
    ? metaChargeText(props.message.pricing, props.message.sentAt)
    : null,
);
const confirmed = computed(() => {
  const at = props.message?.pricing?.cost?.confirmedAt;
  return at
    ? new Date(at).toLocaleDateString(undefined, {
        day: "2-digit",
        month: "2-digit",
      })
    : null;
});

/** "Hoje às 10:23", "Ontem às 18:02", "22/09/2026 às 09:15". */
function when(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return t("delivery.info.at", { day: dateLabel(localDate(d)), time });
}

const rows = computed(() => {
  const m = props.message;
  if (!m) return [];
  const list: Array<{
    key: string;
    label: string;
    value: string;
    icon: "status" | "mic";
    status?: "read" | "delivered" | "sent";
  }> = [
    {
      key: "read",
      label: t("delivery.read"),
      value: when(m.readAt),
      icon: "status",
      status: "read",
    },
  ];
  if (m.kind === "audio" && m.voice)
    list.push({
      key: "played",
      label: t("delivery.played"),
      value: when(m.playedAt),
      icon: "mic",
    });
  list.push(
    {
      key: "delivered",
      label: t("delivery.delivered"),
      value: when(m.deliveredAt ?? m.readAt),
      icon: "status",
      status: "delivered",
    },
    {
      key: "sent",
      label: t("delivery.sent"),
      value: when(m.sentAt),
      icon: "status",
      status: "sent",
    },
  );
  return list;
});
</script>

<template>
  <Modal :open="open" :title="t('delivery.info.title')" @close="emit('close')">
    <div v-if="message" class="message-info">
      <div class="message-info-preview" aria-hidden="true">
        <MessageBubble :message="message" />
      </div>
      <template v-if="delivery === 'failed'">
        <div class="message-info-row failed">
          <MessageStatus status="failed" />
          <span class="grow">
            <strong>{{ t("delivery.failed") }}</strong>
            <span class="message-info-reason">{{ message.failed }}</span>
            <small v-if="message.errorCode" class="muted">{{
              t("delivery.info.code", { code: message.errorCode })
            }}</small>
            <small
              v-if="message.errorDetail"
              class="muted message-info-detail"
              >{{ message.errorDetail }}</small
            >
          </span>
        </div>
        <button
          v-if="message.canRetry"
          type="button"
          class="wa-button secondary compact"
          @click="emit('resend', message.id)"
        >
          {{ t("media.retry") }}
        </button>
      </template>
      <template v-else>
        <div v-for="row in rows" :key="row.key" class="message-info-row">
          <MessageStatus v-if="row.icon === 'status'" :status="row.status!" />
          <Icon v-else name="mic" :size="16" class="message-info-mic" />
          <strong class="grow">{{ row.label }}</strong>
          <span class="muted">{{ row.value }}</span>
        </div>
        <div v-if="message.fromPhone" class="message-info-row">
          <Icon name="phone" :size="16" class="muted" />
          <span class="grow">{{ t("message.from_phone") }}</span>
        </div>
        <div v-if="message.importedFrom" class="message-info-row">
          <Icon name="download" :size="16" class="muted" />
          <span class="grow">{{
            message.authorName
              ? t("message.imported_by", { name: message.authorName })
              : t("message.imported")
          }}</span>
        </div>
        <div v-if="charge" class="message-info-row message-info-charge">
          <Icon name="receipt" :size="16" class="muted" />
          <span class="grow">
            <strong>{{ t("meta.pricing.title") }}</strong>
            <span class="message-info-reason">{{ charge }}</span>
            <small v-if="confirmed" class="green">{{
              t("meta.pricing.confirmed", { day: confirmed })
            }}</small>
            <small v-else-if="message.pricing?.cost" class="muted">{{
              t("meta.pricing.estimate_hint")
            }}</small>
            <small
              v-else-if="message.pricing?.type === 'regular'"
              class="muted"
              >{{ t("meta.pricing.unknown_currency_hint") }}</small
            >
          </span>
        </div>
        <div v-if="trace" class="message-info-trace">
          <h4>
            <Icon name="sparkles" :size="16" />{{ t("message.trace.title") }}
          </h4>
          <p v-if="traceModel" class="small">
            <strong>{{ t("aimodels.trace.model") }}</strong>
            {{ traceModel }}
            <template v-for="f in trace.model?.fallbacks ?? []" :key="f.model">
              ·
              {{
                t(
                  f.reason === "ai_provider_account"
                    ? "aimodels.trace.fallback_account"
                    : "aimodels.trace.fallback",
                  { model: modelName(f.model) },
                )
              }}</template
            >
          </p>
          <p v-if="traceTime" class="small">
            <strong>{{ t("aimodels.trace.time") }}</strong>
            {{ traceTime }}
          </p>
          <p v-if="trace.followUp" class="small">
            <strong>{{ t("message.trace.follow_up") }}</strong>
            {{ t("message.follow_up", trace.followUp) }}
          </p>
          <p v-if="trace.version" class="small">
            <strong>{{ t("message.trace.version") }}</strong>
            {{ t("message.trace.version_number", { number: trace.version }) }}
          </p>
          <p v-if="traceSkills.length" class="small">
            <strong>{{ t("message.trace.skills") }}</strong>
            {{ traceSkills.join(" · ") }}
          </p>
          <p v-if="trace.knowledge.whole.length" class="small">
            <strong>{{ t("message.trace.whole") }}</strong>
            {{ trace.knowledge.whole.join(", ") }}
          </p>
          <p v-if="trace.knowledge.passages.length" class="small">
            <strong>{{ t("message.trace.passages") }}</strong>
            {{ trace.knowledge.passages.join(", ") }}
          </p>
          <p v-if="trace.tools.length" class="small">
            <strong>{{ t("message.trace.tools") }}</strong>
            {{ trace.tools.join(", ") }}
          </p>
          <p class="small muted">{{ traceContext }}</p>
          <RouterLink
            v-if="slug && can(slug, 'settings.manage')"
            to="/settings/ai#skill-events"
            class="small"
            @click="emit('close')"
            >{{ t("message.trace.adjust") }}</RouterLink
          >
        </div>
        <p v-if="delivery === 'pending'" class="small muted">
          {{ t("delivery.help.pending") }}
        </p>
        <p v-else-if="delivery !== 'read'" class="small muted">
          {{ t("delivery.info.no_read") }}
        </p>
      </template>
    </div>
  </Modal>
</template>
